import { useState, useEffect, useRef } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    TextField,
    Grid,
} from "@mui/material";

import ChronoSection from "../components/ChronoSection";
import ChronoTitle from "../components/ChronoTitle";
import ChronoConsole from "../components/ChronoConsole";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// 2 chronos RS232 simultanés — chacun avec son propre port série
// indépendant (Web Serial API), sa propre connexion WebSocket, son propre
// chrono_id. Le port 127.0.0.1 (boîtier réseau émettant déjà des trames
// TN) n'est PAS géré ici : un navigateur ne peut pas ouvrir de socket TCP
// brute — voir le script Python dédié (local_chrono_bridge.py) pour ce
// cas, qui poste directement à /api/events sans passer par cette page.
const SLOT_COUNT = 2;

// ============================================================================
// PARSEURS TAG HEUER + MICROGATE
// ============================================================================
function parseTagHeuer(line) {
    const payload = line.split("\t")[0].trim();
    if (!payload.startsWith("TN")) return null;

    const tokens = payload.slice(2).trim().split(/\s+/);
    if (tokens.length !== 4 && tokens.length !== 5) return null;

    let candidate = null;
    let seq_s, channel, time_s, days_s;

    if (tokens.length === 4) {
        [seq_s, channel, time_s, days_s] = tokens;
    } else {
        [candidate, seq_s, channel, time_s, days_s] = tokens;
        candidate = parseInt(candidate);
    }

    const [hh, mm, rest] = time_s.split(":");
    const [ss, frac] = rest.split(".");
    // ⚠️ frac est une fraction décimale de seconde (peu importe son
    // nombre de chiffres — 5 pour certains chronos RS232, 6 pour
    // Ubidium), PAS un nombre brut de millisecondes. parseInt(frac) était
    // faux : "17500" (0.175s = 175ms) était interprété comme 17500ms
    // (17.5s), faisant dériver l'heure de plusieurs secondes.
    const ms = Math.round(parseFloat("0." + frac) * 1000);

    const days = parseInt(days_s);
    const base = new Date(Date.UTC(2000, 0, 1));
    base.setUTCDate(base.getUTCDate() + days);
    // ⚠️ L'heure du chrono (hh:mm:ss) est déjà l'heure locale de course —
    // aucune conversion de fuseau n'est nécessaire ni voulue. setUTCHours
    // la traitait à tort comme de l'UTC, ce qui ajoutait un décalage
    // fantôme (+2h en horaire d'été suisse) dès que cette valeur revenait
    // s'afficher en heure locale, ou servait à calculer un temps tournant
    // par rapport à Date.now() (vrai instant absolu). setHours interprète
    // correctement hh:mm:ss comme l'heure locale de CE système.
    base.setHours(parseInt(hh), parseInt(mm), parseInt(ss), ms);

    return {
        source_format: "tagheuer",
        sequence: parseInt(seq_s),
        channel,
        candidate: candidate || null,
        timestamp: base.toISOString(),
        raw_value: payload,
    };
}

const MICROGATE_RE =
    /^(?:BR|\x10R)\s*(?<seq>\d{8})(?<candidate>\d{5})(?<pad>\d{5})(?<const1>\d{4})(?<channel>\d{3})(?<const2>\d{3})(?<millis>\d{8})(?<epoch>\d{8})/;

function parseMicrogate(line) {
    const clean = line.trim().replace(/O/g, "0");
    const m = clean.match(MICROGATE_RE);
    if (!m) return null;

    return {
        source_format: "microgate",
        sequence: parseInt(m.groups.seq),
        channel: m.groups.channel,
        candidate: parseInt(m.groups.candidate) || null,
        elapsed_ms: parseInt(m.groups.millis),
        raw_value: line,
    };
}

function parseFrame(line) {
    if (line.startsWith("TN")) return parseTagHeuer(line);
    if (line.startsWith("BR") || line.startsWith("\x10R")) return parseMicrogate(line);
    return null;
}

// ============================================================================
// ENVOI AU SERVEUR
// ============================================================================
async function sendEvent(event) {
    try {
        const res = await fetch(SERVER_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": API_KEY,
            },
            body: JSON.stringify(event),
        });

        if (!res.ok) throw new Error("Serveur indisponible");

        return true;
    } catch (err) {
        throw err;
    }
}

function defaultSlots() {
    return Array.from({ length: SLOT_COUNT }, (_, i) => ({
        chronoId: `chrono-A${i + 1}`,
        connected: false,
        httpOnline: true,
        wsOnline: false,
    }));
}

// ============================================================================
// PAGE PRINCIPALE
// ============================================================================
export default function ConnectChrono() {
    const [slots, setSlots] = useState(defaultSlots);
    const [logs, setLogs] = useState([]);
    const [userName, setUserName] = useState("Christoph");

    const [pendingEvents, setPendingEvents] = useState(() => {
        const saved = localStorage.getItem("pendingEvents");
        return saved ? JSON.parse(saved) : [];
    });

    // Protection contre la fermeture accidentelle de l'onglet (activable
    // manuellement). ⚠️ On ne peut PAS empêcher réellement la fermeture —
    // aucun navigateur ne le permet, par sécurité. On peut seulement
    // déclencher la boîte de dialogue native de confirmation ("Quitter le
    // site ?"). Le texte affiché est imposé par le navigateur (impossible
    // à personnaliser sur Chrome/Firefox/Edge récents) et l'utilisateur
    // peut toujours choisir de quitter quand même.
    const [protectClose, setProtectClose] = useState(false);

    useEffect(() => {
        if (!protectClose) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ""; // requis par certains navigateurs pour afficher la boîte de dialogue
            return "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [protectClose]);

    // État mutable par slot (port, reader, websocket) — dans une ref, pas
    // du state, car ce ne sont pas des données à re-render, juste des
    // handles à conserver entre les appels.
    const slotRefs = useRef(Array.from({ length: SLOT_COUNT }, () => ({ port: null, reader: null, ws: null })));

    const updateSlot = (index, patch) => {
        setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    // Sauvegarde automatique
    useEffect(() => {
        localStorage.setItem("pendingEvents", JSON.stringify(pendingEvents));
    }, [pendingEvents]);

    function addLog(msg) {
        setLogs((prev) => [msg, ...prev.slice(0, 300)]);
    }

    // ========================================================================
    // RETENTATIVE DES TRAMES EN ATTENTE (file partagée entre les 2 slots)
    // ========================================================================
    async function retryPending() {
        if (pendingEvents.length === 0) return;

        const stillPending = [];

        for (const ev of pendingEvents) {
            try {
                await sendEvent(ev);
                addLog("✓ Ré-envoyé HTTP : " + JSON.stringify(ev));
            } catch {
                stillPending.push(ev);
            }
        }

        setPendingEvents(stillPending);
    }

    // Dépendance sur pendingEvents ajoutée (absente dans la version
    // d'origine) — sans ça, l'intervalle capture pour toujours le tableau
    // vide du tout premier rendu et ne retente jamais rien réellement.
    useEffect(() => {
        const interval = setInterval(() => retryPending(), 5000);
        return () => clearInterval(interval);
    }, [pendingEvents]);

    // ========================================================================
    // WEBSOCKET (un par slot, identifié par son propre chrono_id)
    // ========================================================================
    function connectWebSocket(index) {
        const chronoId = slots[index].chronoId;
        try {
            const ws = new WebSocket(`${SERVER_URL.replace("http", "ws")}/ws?chrono_id=${chronoId}`);
            slotRefs.current[index].ws = ws;

            ws.onopen = () => {
                updateSlot(index, { wsOnline: true });
                addLog(`[${chronoId}] 🔌 WebSocket connecté`);
                retryPending();
            };

            ws.onmessage = (msg) => {
                try {
                    const data = JSON.parse(msg.data);

                    if (data.type === "ack") {
                        const seq = data.sequence;
                        const channel = data.channel;

                        addLog(`[${chronoId}] ✓ ACK serveur seq=${seq} canal=${channel}`);

                        // Filtre aussi sur chrono_id — avec 2 chronos actifs,
                        // deux trames différentes peuvent partager la même
                        // séquence+canal ; sans ce filtre, un ACK pourrait
                        // effacer par erreur la trame en attente de l'AUTRE
                        // chrono.
                        setPendingEvents((prev) =>
                            prev.filter(
                                (ev) =>
                                    !(ev.sequence === seq && ev.channel === channel && ev.chrono_id === chronoId)
                            )
                        );
                    }
                } catch {
                    addLog(`[${chronoId}] ⚠ Message WS non JSON`);
                }
            };

            ws.onclose = () => {
                updateSlot(index, { wsOnline: false });
                addLog(`[${chronoId}] ⚠ WebSocket fermé — retry dans 3s`);
                setTimeout(() => connectWebSocket(index), 3000);
            };

            ws.onerror = () => {
                updateSlot(index, { wsOnline: false });
                addLog(`[${chronoId}] ❌ Erreur WebSocket`);
            };
        } catch (err) {
            addLog(`[${chronoId}] ❌ Impossible d'ouvrir WebSocket : ` + err.message);
        }
    }

    // ========================================================================
    // RS232 — par slot
    // ========================================================================
    async function connectPort(index) {
        const chronoId = slots[index].chronoId;
        try {
            const p = await navigator.serial.requestPort();
            await p.open({ baudRate: 9600 });

            slotRefs.current[index].port = p;
            updateSlot(index, { connected: true });
            addLog(`[${chronoId}] ✔ Port série connecté`);

            connectWebSocket(index);

            const textDecoder = new TextDecoderStream();
            p.readable.pipeTo(textDecoder.writable);
            const r = textDecoder.readable.getReader();
            slotRefs.current[index].reader = r;

            readLoop(index, r);
        } catch (err) {
            addLog(`[${chronoId}] ❌ Erreur connexion : ` + err.message);
        }
    }

    async function readLoop(index, reader) {
        const chronoId = slots[index].chronoId;
        try {
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                if (!value) continue;

                const line = value.trim();
                addLog(`[${chronoId}][RS232] ` + line);

                const parsed = parseFrame(line);
                if (!parsed) {
                    addLog(`[${chronoId}] ↺ Ignoré (non reconnu)`);
                    continue;
                }

                const event = {
                    chrono_id: chronoId,
                    channel: parsed.channel,
                    candidate: parsed.candidate,
                    sequence: parsed.sequence,
                    source_format: parsed.source_format,
                    user: userName,
                    raw_value: parsed.raw_value,
                    timestamp: parsed.timestamp || new Date().toISOString(),
                    local_timestamp: new Date().toISOString(),
                };

                addLog(`[${chronoId}] → Envoi : ` + JSON.stringify(event));

                try {
                    await sendEvent(event);
                    updateSlot(index, { httpOnline: true });
                    addLog(`[${chronoId}] ✓ HTTP OK`);
                } catch {
                    updateSlot(index, { httpOnline: false });
                    addLog(`[${chronoId}] ⚠ Stocké (offline)`);
                    setPendingEvents((prev) => [...prev, event]);
                }
            }
        } catch (err) {
            addLog(`[${chronoId}] ❌ Erreur lecture : ` + err.message);
        }
    }

    async function disconnectPort(index) {
        const chronoId = slots[index].chronoId;
        const refs = slotRefs.current[index];
        try {
            if (refs.reader) await refs.reader.cancel();
            if (refs.port) await refs.port.close();
            if (refs.ws) refs.ws.close();

            refs.port = null;
            refs.reader = null;
            refs.ws = null;

            updateSlot(index, { connected: false, wsOnline: false });
            addLog(`[${chronoId}] ✔ Port série déconnecté`);
        } catch (err) {
            addLog(`[${chronoId}] ❌ Erreur déconnexion : ` + err.message);
        }
    }

    // ========================================================================
    // UI
    // ========================================================================
    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <ChronoSection>
                <ChronoTitle>Connexion Chronos RS232 (2 simultanés)</ChronoTitle>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Button
                        variant={protectClose ? "contained" : "outlined"}
                        color={protectClose ? "warning" : "inherit"}
                        size="small"
                        onClick={() => setProtectClose((v) => !v)}
                    >
                        {protectClose
                            ? "🔒 Protection fermeture activée"
                            : "🔓 Activer la protection contre la fermeture"}
                    </Button>
                </Box>

                <TextField
                    label="Utilisateur"
                    fullWidth
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Grid container spacing={3}>
                    {slots.map((slot, i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                    Chrono {i + 1}
                                </Typography>

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: "12px",
                                            bgcolor: slot.httpOnline ? "success.light" : "error.light",
                                            color: slot.httpOnline ? "success.dark" : "error.dark",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {slot.httpOnline ? "HTTP ONLINE" : "HTTP OFFLINE"}
                                    </Box>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: "12px",
                                            bgcolor: slot.wsOnline ? "success.light" : "warning.light",
                                            color: slot.wsOnline ? "success.dark" : "warning.dark",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {slot.wsOnline ? "WS SYNC" : "WS UNSYNC"}
                                    </Box>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: "12px",
                                            bgcolor: slot.connected ? "info.light" : "grey.300",
                                            color: slot.connected ? "info.dark" : "grey.700",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {slot.connected ? "RS232 CONNECTÉ" : "RS232 OFF"}
                                    </Box>
                                </Box>

                                <TextField
                                    label="Nom du chrono"
                                    fullWidth
                                    size="small"
                                    disabled={slot.connected}
                                    value={slot.chronoId}
                                    onChange={(e) => updateSlot(i, { chronoId: e.target.value })}
                                    sx={{ mb: 2 }}
                                />

                                {!slot.connected ? (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                        onClick={() => connectPort(i)}
                                    >
                                        Connecter (RS232)
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        onClick={() => disconnectPort(i)}
                                    >
                                        Stop
                                    </Button>
                                )}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </ChronoSection>

            <ChronoSection>
                <ChronoTitle>Journal RS232</ChronoTitle>

                <ChronoConsole lines={logs} />

                {pendingEvents.length > 0 && (
                    <Paper sx={{ p: 2, mt: 3, borderRadius: "12px" }}>
                        <Typography variant="subtitle2" color="warning.main">
                            Trames en attente ({pendingEvents.length})
                        </Typography>
                        {pendingEvents.map((ev, i) => (
                            <Typography key={i} sx={{ fontSize: "0.8rem" }}>
                                ⚠ {JSON.stringify(ev)}
                            </Typography>
                        ))}
                    </Paper>
                )}
            </ChronoSection>
        </Container>
    );
}